// Structure placement planner (STR-1/STR-4). PURE (ENG-1).
//
// Each structure type is scattered on a grid of `spacing`×`spacing`-chunk regions:
// one candidate chunk per region, its offset picked by a seeded positional stream
// so the same (seed, region, salt) always yields the same candidate — on the main
// thread, in the gen worker, and on the server. `separation` keeps candidates off
// the far region edge so two neighbours never abut. An `accept` predicate (biome +
// flatness, supplied by the generator) turns a candidate into a real structure.
// `/locate` (STR-4) walks these exact regions outward and returns the nearest
// accepted candidate — the command and worldgen share one source of truth.
import { WORLD } from '../config';
import { Rng } from '../engine/rng';

export interface StructurePlacement {
  /** Unique salt for this structure's positional stream (E-25 declared elsewhere). */
  salt: string;
  /** Region size in chunks (grid pitch). */
  spacing: number;
  /** Min chunks kept clear of the far region edge (offset range = spacing−separation). */
  separation: number;
}

/** floorDiv for negative-safe region indexing. */
const floorDiv = (a: number, b: number): number => Math.floor(a / b);

/**
 * The candidate start-chunk for the region (rx,rz). Deterministic in
 * (seed, salt, rx, rz): two positional rng draws pick the in-region offset.
 */
export function regionCandidate(
  seed: number | string | bigint,
  p: StructurePlacement,
  rx: number,
  rz: number,
): [number, number] {
  const range = Math.max(1, p.spacing - p.separation);
  const rng = new Rng(seed).forkAt(p.salt, rx, rz);
  const offX = rng.nextInt(range);
  const offZ = rng.nextInt(range);
  return [rx * p.spacing + offX, rz * p.spacing + offZ];
}

/** The region indices a chunk belongs to. */
export function regionOf(p: StructurePlacement, cx: number, cz: number): [number, number] {
  return [floorDiv(cx, p.spacing), floorDiv(cz, p.spacing)];
}

/**
 * If a structure's candidate falls on chunk (cx,cz), return that candidate — i.e.
 * "does this chunk own a structure's start?" (before the accept predicate).
 */
export function candidateStartingAt(
  seed: number | string | bigint,
  p: StructurePlacement,
  cx: number,
  cz: number,
): boolean {
  const [rx, rz] = regionOf(p, cx, cz);
  const [ccx, ccz] = regionCandidate(seed, p, rx, rz);
  return ccx === cx && ccz === cz;
}

/** A located structure: its origin chunk + the world block coords of that chunk's
 *  NW (min) corner (y unresolved — the generator sets it per ground mode). */
export interface Located {
  cx: number;
  cz: number;
  x: number;
  z: number;
}

/**
 * Nearest accepted candidate to (fromCx,fromCz), searching regions in rings of
 * increasing Chebyshev radius (STR-4). `accept` re-checks biome/flatness exactly
 * as worldgen does. Returns null if none is found within `maxRings`.
 */
export function locateStructure(
  seed: number | string | bigint,
  p: StructurePlacement,
  fromCx: number,
  fromCz: number,
  accept: (cx: number, cz: number) => boolean,
  maxRings = 100,
): Located | null {
  const [r0x, r0z] = regionOf(p, fromCx, fromCz);
  let best: Located | null = null;
  let bestDist = Infinity;
  for (let ring = 0; ring <= maxRings; ring++) {
    let ringHit = false;
    for (let rz = r0z - ring; rz <= r0z + ring; rz++) {
      for (let rx = r0x - ring; rx <= r0x + ring; rx++) {
        // Only the perimeter of each ring is new.
        if (ring > 0 && Math.abs(rx - r0x) !== ring && Math.abs(rz - r0z) !== ring) continue;
        const [ccx, ccz] = regionCandidate(seed, p, rx, rz);
        if (!accept(ccx, ccz)) continue;
        ringHit = true;
        const dx = ccx - fromCx, dz = ccz - fromCz;
        const dist = dx * dx + dz * dz;
        if (dist < bestDist) {
          bestDist = dist;
          best = { cx: ccx, cz: ccz, x: ccx * WORLD.SECTION, z: ccz * WORLD.SECTION };
        }
      }
    }
    // Once a ring produced a hit, one more ring guarantees we've seen the true
    // nearest (a closer candidate can hide one ring further along a diagonal).
    if (best && (ringHit || ring > 0) && ring >= 1 && bestDist <= (ring * p.spacing) ** 2) break;
  }
  return best;
}
